import React from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import { useStateSelector } from "utils/field";
import { useSubcomponentDirectionLocked } from "features/Model/Enclosure/_utils/useEffectiveFacingDirection";
import { required, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { getOptions, getBaseUnits, getUnitOptions } from "utils/fields";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";

import classes from "./style.module.scss";

const doorTypeValidation = [required, ...getValidation("doorType")];
const orientationValidation = getValidation("doorDirection");

const Construction = React.memo(
    ({ accessor, parentAccessor, change, canBeEditedFromParametric = true, isCompact = false, codeRefs = [] }) => {
        const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
        const { id: doorTypeId, value: doorRValue } = useStateSelector(`${accessor}.doorType`) || {};
        const rValUnits = useStateSelector(`${accessor}.doorType_u`);
        const enerStar = useStateSelector(`${accessor}.energyStar`);
        const parentAdjacent = useStateSelector(`${parentAccessor}.adjacentEnclosedSpace`);
        const { id: doorFacingId } = useStateSelector(`${accessor}.facingDirection`) || {};
        const isDisableDoorDir = useSubcomponentDirectionLocked("door", accessor, parentAccessor);

        return (
            <>
                <InputRow gridTemplate={isCompact ? "1fr 1fr 1fr 1fr" : "31.85% 23.25% 23.25%"} compact={isCompact}>
                    <Field
                        component={Select}
                        name={`${accessor}.doorType`}
                        options={[
                            ...getOptions({ fieldKey: "doorType" }),
                            {
                                label: "User Specified",
                                value: {
                                    id: 7,
                                    value: doorRValue, //need to get rValue for this to match
                                },
                            },
                        ].map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value.codeRef),
                        }))}
                        label="Door Type"
                        placeholder="Choose Door Type"
                        validate={doorTypeValidation}
                        disabled={!canBeEditedFromParametric}
                        compact={isCompact}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.doorType.value`}
                        label="R-Value"
                        placeholder={0.0}
                        disabled={doorTypeId !== 7 || !canBeEditedFromParametric}
                        validate={doorTypeValidation}
                        setTouched={true}
                        decimals={getDecimalPlaces("doorType")}
                        change={change}
                        setValue={doorRValue ? doorRValue : 0}
                        units={{
                            base: getBaseUnits("doorType", modelUnits),
                            options: getUnitOptions("thermalResistance"),
                            selected: rValUnits,
                            unitType: "thermalResistance",
                            accessor: `${accessor}.doorType_u`,
                        }}
                        compact={isCompact}
                    />
                    {isCompact ? (
                        <>
                            <Field
                                component={Checkbox}
                                name={`${accessor}.energyStar`}
                                label="Energy Star"
                                large
                                className={classes.enerStar}
                                type="checkbox"
                                disabled={!canBeEditedFromParametric}
                                compact
                            />
                            <Field
                                component={Select}
                                name={`${accessor}.facingDirection`}
                                options={getOptions({ fieldKey: "doorDirection" }).map((option) => ({
                                    ...option,
                                    disabled: codeRefs.includes(option.value?.codeRef),
                                }))}
                                label="Facing Direction"
                                placeholder="Choose Facing Direction"
                                validate={orientationValidation}
                                disabled={isDisableDoorDir}
                                compact
                                compassProps={{
                                    facingId: doorFacingId,
                                    disabled: isDisableDoorDir,
                                    rotationOptions: {
                                        0: 180,
                                        1: 135,
                                        2: 90,
                                        3: 45,
                                        4: 0,
                                        5: -45,
                                        6: -90,
                                        7: -135,
                                    },
                                }}
                            />
                        </>
                    ) : (
                        <Field
                            component={Checkbox}
                            name={`${accessor}.energyStar`}
                            label="Energy Star"
                            large
                            className={classes.enerStar}
                            type="checkbox"
                            disabled={!canBeEditedFromParametric}
                        />
                    )}
                </InputRow>
                {isCompact && (
                    <InputRow gridTemplate="1fr" compact lastRow>
                        <Field
                            className={classes.adjacentCheckbox}
                            component={Checkbox}
                            name={`${accessor}.adjacentEnclosedSpace`}
                            label="Adjacent to enclosed unconditioned space"
                            large
                            disabled={true}
                            input={{
                                value: parentAdjacent,
                            }}
                            type="checkbox"
                            compact
                        />
                    </InputRow>
                )}
            </>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Construction);
